import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "@/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FieldError } from "@/components/ui/field";
import { CircleHelp, Loader2 } from "lucide-react";
import { stepChanged, customerDataSaved, reachedTerminal, receivedProgramsChanged } from "@/app/booking-flow/publicBookingFlowSlice";
import { bookingSeasonClosedTerminal } from "@/app/booking-flow/bookingSeasonGate";
import PhoneInput, { isValidPhoneNumber, type Value } from 'react-phone-number-input';
import {
  useVerifyPhoneNumberMutation,
  useCheckUpcomingHouseholdBookingMutation,
  useCheckProgramsMutation,
  useClearProgramCheckMutation,
} from "@/app/booking-flow/publicBookingFlowApi";
import {
  evaluateIdentifyingInfo,
  REQUIRED_FIELDS_FOOTNOTE,
  SSN_DIALOG_TEXT,
  SSN_HELPER_TEXT,
} from "./identifyingInfoRules";

export const IdentifyingInfoStep = () => {
  const dispatch = useAppDispatch();

  const savedFirstName = useAppSelector(state => state.publicBookingFlow.customerData?.firstName);
  const savedLastName = useAppSelector(state => state.publicBookingFlow.customerData?.lastName);
  const savedSsn = useAppSelector(state => state.publicBookingFlow.customerData?.ssn);
  const savedDateOfBirth = useAppSelector(state => state.publicBookingFlow.customerData?.dateOfBirth);
  const savedEmail = useAppSelector(state => state.publicBookingFlow.customerData?.email);
  const savedPhone = useAppSelector(state => state.publicBookingFlow.customerData?.phone);
  const savedSmsOptIn = useAppSelector(state => state.publicBookingFlow.customerData?.smsOptIn);
  const savedEnergyAccountNumber = useAppSelector(state => state.publicBookingFlow.customerData?.energyAccountNumber);

  const [triggerVerifyPhoneNumber] = useVerifyPhoneNumberMutation();
  const [triggerUpcomingHouseholdCheck] = useCheckUpcomingHouseholdBookingMutation();
  const [triggerProgramCheck] = useCheckProgramsMutation();
  const [triggerClearProgramCheck] = useClearProgramCheckMutation();

  const [firstNameInput, setFirstNameInput] = useState(savedFirstName ?? "");
  const [lastNameInput, setLastNameInput] = useState(savedLastName ?? "");
  const [ssnInput, setSsnInput] = useState(savedSsn ?? "");
  const [ssnTouched, setSsnTouched] = useState(false);
  const [dateOfBirthInput, setDateOfBirthInput] = useState(savedDateOfBirth ?? "");
  const [dateOfBirthTouched, setDateOfBirthTouched] = useState(false);
  const [emailInput, setEmailInput] = useState(savedEmail ?? "");
  const [phoneInput, setPhoneInput] = useState<Value | undefined>(savedPhone);
  // Unchecked unless the applicant checked it themselves. `savedSmsOptIn` only carries their own
  // earlier choice back when they step forward and back through the flow, so it is never a default.
  const [smsOptIn, setSmsOptIn] = useState(savedSmsOptIn ?? false);
  const [energyAccountNumberInput, setEnergyAccountNumberInput] = useState(savedEnergyAccountNumber ?? "");
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [phoneVerifyError, setPhoneVerifyError] = useState<string | undefined>();

  const phoneValid = !!phoneInput && isValidPhoneNumber(phoneInput) && phoneInput.replace(/\D/g, "").length <= 11;
  const emailValid = emailInput === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput);

  // Optional, but if it is filled in it has to be a date an adult applicant could have been born on.
  // The server applies the same rule and simply ignores anything outside it, so this is here to tell
  // somebody they fat-fingered the year rather than to enforce anything.
  const dateOfBirthValid = (() => {
    if (dateOfBirthInput === "") return true;
    const parsed = new Date(`${dateOfBirthInput}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return false;
    const sixteenYearsAgo = new Date();
    sixteenYearsAgo.setFullYear(sixteenYearsAgo.getFullYear() - 16);
    return parsed.getFullYear() > 1901 && parsed <= sixteenYearsAgo;
  })();

  const { ssnValid, canSubmit } = evaluateIdentifyingInfo({
    firstName: firstNameInput,
    lastName: lastNameInput,
    ssn: ssnInput,
    phoneValid,
    emailValid,
    dateOfBirthValid,
  });

  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ssnValid) {
      setSsnTouched(true);
      return;
    }

    if (!dateOfBirthValid) {
      setDateOfBirthTouched(true);
      return;
    }

    if (!phoneValid) {
      setPhoneTouched(true);
      return;
    }

    if (!emailValid) {
      setEmailTouched(true);
      return;
    }

    setLoading(true);

    try {
      // ── Phase 0: Phone reachability check ──
      setStatusText("Verifying phone number…");
      const phoneLookup = await triggerVerifyPhoneNumber(phoneInput!).unwrap();
      if (!phoneLookup.valid) {
        setPhoneTouched(true);
        setPhoneVerifyError(phoneLookup.message || "We couldn't verify this phone number. Please check it and try again.");
        return;
      }

      dispatch(customerDataSaved({
        firstName: firstNameInput,
        lastName: lastNameInput,
        ssn: ssnInput,
        dateOfBirth: dateOfBirthInput,
        email: emailInput,
        phone: phoneInput,
        smsOptIn: smsOptIn,
        energyAccountNumber: energyAccountNumberInput
      }))

      // ── Phase 1: Does this household already have an appointment? ──
      //
      // MSC's rule: a Social Security number matching one on a valid upcoming appointment, of any
      // event type, blocks the booking. Asked here rather than only at booking time so somebody who
      // is already on the calendar is not made to fill in their household income and their heating
      // source before finding out.
      //
      // This is NOT the block — the server refuses at POST /api/ams/bookings with a 409, and
      // AppointmentBookingStep catches that. This is the courtesy that makes the refusal land early,
      // which is why a failure here is swallowed: an endpoint having a bad moment must not stop a
      // person who has no appointment from making one, and anyone who does have one still gets
      // stopped two steps later by the server. The catch is scoped to this call for that reason.
      if (ssnInput !== "") {
        try {
          setStatusText("Checking for an existing appointment…");
          const existing = await triggerUpcomingHouseholdCheck({ ssn: ssnInput }).unwrap();
          if (existing.found) {
            dispatch(reachedTerminal({
              type: "hh_existing_appointment",
              detail: { noticeId: existing.noticeId, phoneLast4: existing.phoneLast4 },
            }));
            return;
          }
        } catch (err) {
          // One refusal is worth telling them about: booking has closed since they started, and
          // carrying on would only have them fill in their household for a create that is refused.
          const seasonClosed = bookingSeasonClosedTerminal(err);
          if (seasonClosed) {
            dispatch(reachedTerminal(seasonClosed));
            return;
          }
          // Otherwise nothing to tell the applicant: as far as they are concerned the booking is
          // proceeding, which it is. A duplicate appointment is a staff matter and the review queue
          // still catches it at booking time.
        }
      }

      // ── Phase 2: Is any program left for this household this program year? (AB#590) ──
      //
      // A household that has already received every program it could be booked for is refused at
      // POST /api/ams/bookings (409 programAlreadyReceived). Asked here so they find out before the
      // household questions, with the reason and what to do next. Like the check above it is a
      // courtesy, not the block, so a failure is swallowed and the server still refuses later.
      //
      // Asked on every submit, with the number as typed (empty when there is none): the server keeps
      // the answer against the session for the guidelines step, and only a fresh check replaces it,
      // so a number removed or changed on the way back through this step (or after Start Over) must
      // not leave the earlier answer behind. With no number the server answers applies: false.
      try {
        setStatusText("Checking your household's programs…");
        const programs = await triggerProgramCheck({ ssn: ssnInput }).unwrap();
        if (programs.noProgramRemains) {
          dispatch(reachedTerminal({ type: "hh_no_pledges", detail: { message: programs.message ?? null } }));
          return;
        }
        dispatch(receivedProgramsChanged(programs.receivedPrograms ?? []));
      } catch (err) {
        // Whatever went wrong, a 429 included, the server may still hold an earlier answer: ask it to
        // forget it. Fire-and-forget; a failure here costs nothing worse than the earlier answer.
        void triggerClearProgramCheck().unwrap().catch(() => {});
        const seasonClosed = bookingSeasonClosedTerminal(err);
        if (seasonClosed) {
          dispatch(reachedTerminal(seasonClosed));
          return;
        }
        // Nothing known: offer every program, and let the server decide at booking time.
        dispatch(receivedProgramsChanged([]));
      }

      dispatch(stepChanged(3));
    } catch {
      dispatch(reachedTerminal({ type: "error", detail: { message: "Something went wrong. Please try again." } }));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    dispatch(stepChanged(1));
  };

  return (
    <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
      <CardHeader className="text-center px-4">
        <CardTitle className="text-2xl font-semibold text-slate-900">Begin Scheduling an Appointment</CardTitle>
        <CardDescription className="text-slate-500">
          There is an appointment slot currently available in your area. To secure the appointment, please provide the following information. Please, complete the booking before this session expires.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form className="space-y-8" id="personal-info-step" onSubmit={handleSubmit}>
          <div className="flex gap-3">
            <div className="space-y-2 flex-1">
              <Label htmlFor="firstName">Enter Your First Name<span aria-hidden="true" className="text-destructive">*</span></Label>
              <Input id="firstName" name="firstName" type="text" placeholder="First Name" value={firstNameInput} onChange={(e) => setFirstNameInput(e.target.value)} required />
            </div>

            <div className="space-y-2 flex-1">
              <Label htmlFor="lastName">Last Name<span aria-hidden="true" className="text-destructive">*</span></Label>
              <Input id="lastName" name="lastName" type="text" placeholder="Last Name" value={lastNameInput} onChange={(e) => setLastNameInput(e.target.value)} required />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="ssn">Social Security Number</Label>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 text-slate-400 hover:text-slate-600"
                    aria-label="Why we ask for a Social Security Number"
                  >
                    <CircleHelp className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Social Security Number</DialogTitle>
                    <DialogDescription>
                      {SSN_DIALOG_TEXT}
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>
            <p id="ssn-note" className="text-sm text-slate-500">
              {SSN_HELPER_TEXT}
            </p>
            <Input
              id="ssn"
              name="ssn"
              type="password"
              // Without this, a type="password" field with no autocomplete hint prompts the browser
              // to save the value to the password manager.
              autoComplete="off"
              inputMode="numeric"
              pattern="\d{9}"
              maxLength={9}
              placeholder="•••••••••"
              value={ssnInput}
              onChange={(e) => setSsnInput(e.target.value.replace(/\D/g, "").slice(0, 9))}
              onBlur={() => setSsnTouched(true)}
              aria-describedby="ssn-note"
              aria-invalid={ssnTouched && !ssnValid}
            />
            {ssnTouched && !ssnValid && (
              <FieldError>Social Security Number must be 9 digits.</FieldError>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number <span aria-hidden="true" className="text-destructive">*</span></Label>
            <p id="phone-note" className="text-sm text-slate-500">
              Enter your phone number which we can contact you with and send information to.
            </p>
            <PhoneInput
              id="phone"
              name="phone"
              placeholder="Enter phone number"
              aria-describedby="phone-note"
              defaultCountry="US"
              value={phoneInput}
              onChange={(value) => { setPhoneInput(value); setPhoneVerifyError(undefined); }}
              onBlur={() => setPhoneTouched(true)}
              className="border-input dark:bg-input/30 [&.PhoneInput--focus]:border-ring [&.PhoneInput--focus]:ring-ring/50 [&.PhoneInput--focus]:ring-[3px] flex h-9 w-full min-w-0 items-center gap-2 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm"
              numberInputProps={{
                className: "placeholder:text-muted-foreground min-w-0 flex-1 border-0 bg-transparent p-0 text-base shadow-none outline-none md:text-sm",
              }}
            />
            {phoneTouched && !phoneValid && (
              <FieldError>Enter a valid phone number.</FieldError>
            )}
            {phoneTouched && phoneValid && phoneVerifyError && (
              <FieldError>{phoneVerifyError}</FieldError>
            )}
            {/*
              Text message opt-in. The box starts unchecked and stays that way until the applicant
              checks it — a pre-checked box is not consent, and it is the first thing carrier review
              rejects an opt-in for. Everything a reviewer needs to see beside the box is here:
              what we send, how often, what it costs, how to stop, and where the terms are.
            */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="smsOptIn"
                  name="smsOptIn"
                  className="mt-0.5"
                  checked={smsOptIn}
                  onCheckedChange={(checked) => setSmsOptIn(checked === true)}
                  aria-describedby="sms-opt-in-details"
                />
                <Label htmlFor="smsOptIn" className="text-sm font-medium text-slate-900 leading-snug">
                  Yes, sign me up for text messages about my appointment at the phone number above.
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              aria-invalid={emailTouched && !emailValid}
            />
            {emailTouched && !emailValid && (
              <FieldError>Enter a valid email address.</FieldError>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="dateOfBirth">Date of Birth</Label>
            <Input
              id="dateOfBirth"
              name="dateOfBirth"
              type="date"
              value={dateOfBirthInput}
              onChange={(e) => setDateOfBirthInput(e.target.value)}
              onBlur={() => setDateOfBirthTouched(true)}
              aria-invalid={dateOfBirthTouched && !dateOfBirthValid}
            />
            {dateOfBirthTouched && !dateOfBirthValid && (
              <FieldError>Enter the date of birth of the person applying, or leave it blank.</FieldError>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="energyAccountNumber">Energy Account Number</Label>
            <p className="text-sm text-slate-500">
              Enter the account number from your energy provider. If you do not have an account number, leave this field blank.
            </p>
            <Input id="energyAccountNumber" name="energyAccountNumber" type="text" placeholder="Optional" value={energyAccountNumberInput} onChange={(e) => setEnergyAccountNumberInput(e.target.value)} />
          </div>

          <p className="text-sm text-destructive text-center">
            {REQUIRED_FIELDS_FOOTNOTE}
          </p>
        </form>
      </CardContent>

      <CardFooter className="px-4 pb-6 flex flex-col gap-2">
        {/* Sits with the button that submits the consent, which is where carrier review expects it. */}
        {/* <p className="text-sm text-slate-500 max-w-md mx-auto text-center">
          {smsProgram.consentStatement}
        </p> */}

        <div className="flex flex-col sm:flex-row gap-3 mt-6 w-full max-w-md mx-auto">
          <Button type="button" variant="outline" className="flex flex-grow-1 text-base h-12" onClick={handleBack}>
            Back
          </Button>
          <Button form="personal-info-step" type="submit" className="flex flex-grow-2 text-base h-12" disabled={loading || !canSubmit}>
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {statusText}
              </span>
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
